import { Badge, Center, Group, Loader, Menu, Text, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBrush, IconCaretDownFilled, IconCopy, IconListCheck, IconUser } from "@tabler/icons-react";
import * as React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { identifyUser, resetAnalytics } from "../analytics/analytics";
import { useMe } from "../api/MeContext";
import { signOut } from "../auth/signIn";
import { env } from "../env";
import { DisabledNotice } from "../pages/DisabledNotice";
import { ErrorState } from "../pages/ErrorState";
import { NavItem } from "../ui/shell/NavItem";
import { PanelShell } from "../ui/shell/PanelShell";
import { accountMenuItems, navItems } from "./nav";

const NAV_ICON = { members: IconUser, standards: IconBrush, library: IconCopy, activity: IconListCheck } as const;

/** The signed-in shell (Figma admin frames): the wordmark and the account menu on top, the
 * page list on the left, the footer below. Wiring only; the frame is `PanelShell`. */
export function AppLayout({ email }: { email: string }) {
  const { state, reload } = useMe();
  const [navOpen, { toggle, close }] = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const me = state.status === "ready" ? state.me : null;

  React.useEffect(() => {
    if (state.status === "ready") identifyUser(email, state.me);
  }, [state, email]);

  const onSignOut = async () => {
    await signOut();
    resetAnalytics();
    navigate("/sign-in", { replace: true });
  };

  const accountMenu = (
    <Group gap="sm" wrap="nowrap">
      {env.envLabel ? <Badge color="orange">{env.envLabel}</Badge> : null}
      <Menu position="bottom-end" width={240}>
        <Menu.Target>
          <UnstyledButton aria-label="Account menu">
            <Group gap={5} wrap="nowrap">
              <Text lh={1.25}>Account</Text>
              <IconCaretDownFilled size={10} aria-hidden />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>{me ? `${email} · ${me.orgName}` : email}</Menu.Label>
          {accountMenuItems(me).map((item) => (
            <Menu.Item key={item.to} component={Link} to={item.to}>
              {item.label}
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item onClick={() => void onSignOut()}>Sign out</Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );

  const nav = me
    ? navItems(me).map((item) => (
        <NavItem
          key={item.to}
          to={item.to}
          label={item.label}
          icon={NAV_ICON[item.key]}
          active={location.pathname === item.to}
          onNavigate={close}
        />
      ))
    : null;

  return (
    <PanelShell headerEnd={accountMenu} nav={nav} navOpen={navOpen} onNavToggle={toggle} onNavClose={close}>
      {state.status === "loading" ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : null}
      {state.status === "error" ? <ErrorState message={state.message} onRetry={reload} /> : null}
      {me && me.status === "disabled" && location.pathname !== "/account" ? <DisabledNotice /> : null}
      {me && (me.status !== "disabled" || location.pathname === "/account") ? <Outlet /> : null}
    </PanelShell>
  );
}
